import React from 'react';
import type { SequelOperation, SequelOperationStatus } from '../src/types';

interface SequelOperationPanelProps {
  open: boolean;
  operation: SequelOperation | null;
  stepMessage: string | null;
  busy: boolean;
  onResume: () => void;
  onCancelSetup: () => void;
  onDismiss: () => void;
}

function statusLabel(status: SequelOperationStatus | undefined): string {
  switch (status) {
    case 'PENDING':
      return 'Preparing sequel operation…';
    case 'EVOLVING_LEDGER':
      return 'Evolving ledger continuity…';
    case 'EVOLVING_BIBLE':
      return 'Evolving story blueprint…';
    case 'READY':
      return 'Volume setup complete';
    case 'UNKNOWN_OUTCOME':
      return 'Setup paused — outcome unknown';
    case 'FAILED':
      return 'Setup failed';
    case 'CANCEL_REQUESTED':
      return 'Cancellation requested';
    default:
      return 'Volume setup is still processing';
  }
}

const SequelOperationPanel: React.FC<SequelOperationPanelProps> = ({
  open,
  operation,
  stepMessage,
  busy,
  onResume,
  onCancelSetup,
  onDismiss,
}) => {
  if (!open || !operation) return null;

  const canResume =
    !busy &&
    (operation.status === 'UNKNOWN_OUTCOME' ||
      operation.status === 'FAILED' ||
      operation.status === 'PENDING' ||
      operation.status === 'EVOLVING_LEDGER' ||
      operation.status === 'EVOLVING_BIBLE');

  const showUnknown =
    operation.status === 'UNKNOWN_OUTCOME' ||
    operation.errorCode === 'TIMEOUT_UNKNOWN_OUTCOME';

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-lg rounded-xl border border-cv-border-strong bg-cv-panel p-6 shadow-2xl">
        <h2 className="text-lg font-bold text-cv-primary">Volume {operation.targetVolume} Setup</h2>
        <p className="mt-2 text-sm text-cv-secondary">{statusLabel(operation.status)}</p>
        {stepMessage && <p className="mt-1 text-xs text-cv-interactive">{stepMessage}</p>}
        {showUnknown && (
          <p className="mt-3 text-sm text-cv-warning">
            The browser lost contact with the generation service before a definitive result was returned.
            This does not mean failure. Resume will query the saved operation and continue only incomplete steps.
          </p>
        )}
        <div className="mt-4 rounded-lg border border-cv-border bg-cv-page/60 p-3 text-xs text-cv-muted font-mono space-y-1">
          <div>Operation: {operation.operationId}</div>
          <div>Target project: {operation.targetProjectId}</div>
          <div>Completed: {operation.completedSteps.join(', ') || 'none'}</div>
        </div>
        <div className="mt-6 flex flex-wrap gap-2 justify-end">
          {operation.status !== 'CANCEL_REQUESTED' && (
            <button
              type="button"
              onClick={onCancelSetup}
              disabled={busy}
              className="px-4 py-2 text-xs font-bold uppercase tracking-wider rounded border border-cv-border-strong text-cv-secondary hover:bg-cv-elevated disabled:opacity-40"
            >
              Cancel Setup
            </button>
          )}
          {canResume && (
            <button
              type="button"
              onClick={onResume}
              disabled={busy}
              className="px-4 py-2 text-xs font-bold uppercase tracking-wider rounded bg-cv-interactive hover:bg-cv-interactive-hover text-cv-page disabled:opacity-40"
            >
              {busy ? 'Processing…' : 'Resume'}
            </button>
          )}
          <button
            type="button"
            onClick={onDismiss}
            className="px-4 py-2 text-xs font-bold uppercase tracking-wider rounded border border-cv-border-strong text-cv-muted hover:text-cv-primary"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SequelOperationPanel;
